import evaluateFieldConditions from '../evaluate-field-conditions';

export const toList = value => {
  if (Array.isArray(value)) return value;
  if (value != null && value !== '') return [value];
  return [];
};

export const syncFixedArrayFromSource = (existingArray, sourceValues, syncKeyField) => {
  const keys = toList(sourceValues);
  const existing = Array.isArray(existingArray) ? existingArray : [];
  const byKey = {};

  for (const item of existing) {
    if (item && typeof item === 'object' && item[syncKeyField] != null) {
      byKey[item[syncKeyField]] = item;
    }
  }

  return keys.map(key => {
    const prev = byKey[key];
    if (prev) return { ...prev, [syncKeyField]: key };
    return { [syncKeyField]: key };
  });
};

const hasSubFieldValue = (subField, value) => {
  if (value === undefined || value === null) return false;
  if (subField?.type === 'options_list') {
    const options = value?.options;
    return Array.isArray(options) && options.length > 0;
  }
  return true;
};

export const applyArraySubFieldDefaults = (arrayField, items, formValues = {}) => {
  const subFields = arrayField?.subFields ?? arrayField?.sub_fields ?? [];
  const arrayCode = arrayField?.code;
  if (!Array.isArray(items) || !subFields.length) return items;

  const arraySubFieldCodes = subFields.map(subField => subField.code).filter(Boolean);

  return items.map((item, arrayIndex) => {
    const next = { ...item };

    const buildConditionValues = () => ({
      ...formValues,
      [arrayCode]: items.map((existingItem, idx) => (idx === arrayIndex ? next : existingItem)),
    });

    const isVariantVisible = subField => {
      const conditions = subField.conditions ?? subField.condition;
      if (!conditions || !arrayCode) return true;
      return evaluateFieldConditions(conditions, buildConditionValues(), {
        parentCode: arrayCode,
        arrayIndex,
        arraySubFieldCodes,
      });
    };

    const variantsByCode = new Map();
    for (const subField of subFields) {
      const variants = variantsByCode.get(subField.code) ?? [];
      variants.push(subField);
      variantsByCode.set(subField.code, variants);
    }

    const maxPasses = variantsByCode.size;
    for (let pass = 0; pass < maxPasses; pass++) {
      let changedInPass = false;
      for (const [code, variants] of variantsByCode) {
        const visibleVariants = variants.filter(isVariantVisible);
        if (!visibleVariants.length) {
          if (code in next) {
            delete next[code];
            changedInPass = true;
          }
          continue;
        }
        const representativeVariant = visibleVariants[0];
        if (hasSubFieldValue(representativeVariant, next[code])) continue;
        const variantWithDefault = visibleVariants.find(
          variant => (variant.defaultValue ?? variant.default_value) != null,
        );
        if (variantWithDefault) {
          next[code] = variantWithDefault.defaultValue ?? variantWithDefault.default_value;
          changedInPass = true;
        }
      }
      if (!changedInPass) break;
    }

    return next;
  });
};

const DEFAULT_OPTION_KEYS = new Set(['name', 'value', 'order']);

export const resolveExtendFieldsForSourceOptions = (extendFields, sourceOptions) => {
  if (Array.isArray(extendFields) && extendFields.length) return extendFields;
  if (!Array.isArray(sourceOptions)) return [];
  const sample = sourceOptions.find(opt => opt && typeof opt === 'object');
  if (!sample) return [];
  return Object.keys(sample).filter(key => !DEFAULT_OPTION_KEYS.has(key));
};

export const enrichArrayItemsFromSourceOptions = (
  items,
  syncKeyField,
  sourceOptions,
  extendFields = [],
) => {
  const fieldsToCopy = resolveExtendFieldsForSourceOptions(extendFields, sourceOptions);
  if (!Array.isArray(items) || !fieldsToCopy.length || !Array.isArray(sourceOptions)) {
    return items;
  }

  return items.map(item => {
    if (!item || item[syncKeyField] == null) return item;
    const match = sourceOptions.find(
      opt => opt?.value === item[syncKeyField] || String(opt?.value) === String(item[syncKeyField]),
    );
    if (!match) return item;

    const next = { ...item };
    for (const field of fieldsToCopy) {
      if (match[field] !== undefined && match[field] !== null) {
        next[field] = match[field];
      }
    }
    return next;
  });
};

export const syncArrayFieldItems = (
  arrayField,
  existingArray,
  sourceValues,
  formValues,
  optionsLookup = {},
  sourceField = null,
) => {
  const sizeSource = arrayField.sizeSource || arrayField.size_source;
  const syncKey = arrayField.syncKeyField || arrayField.sync_key_field;
  const extendFields =
    sourceField?.options?.extendFields ?? sourceField?.options?.extend_fields ?? [];

  let items = syncFixedArrayFromSource(existingArray, sourceValues, syncKey);
  items = enrichArrayItemsFromSourceOptions(
    items,
    syncKey,
    optionsLookup[sizeSource] ?? [],
    extendFields,
  );
  items = applyArraySubFieldDefaults(arrayField, items, {
    ...formValues,
    [arrayField.code]: items,
  });
  return items;
};

export const resolveArraySize = (formValues, sizeSource) => toList(formValues?.[sizeSource]).length;

export const resolveArrayItemTitles = (
  formValues,
  titlesSource,
  optionsLookup = {},
  selectedOptionsContext = {},
) => {
  const values = toList(formValues?.[titlesSource]);
  if (!values.length) return [];

  const options = optionsLookup[titlesSource] ?? [];
  return values.map(value => {
    const selected = selectedOptionsContext[titlesSource];
    if (Array.isArray(selected)) {
      const match = selected.find(opt => opt?.value === value);
      if (match?.name) return match.name;
    } else if (selected?.value === value && selected?.name) {
      return selected.name;
    }
    const match = options.find(opt => opt?.value === value);
    return match?.name ?? value;
  });
};
